// 3D preview of a Mesh: WebGL, no library. Drag to turn, wheel or pinch to
// zoom, right-drag / two fingers / Shift+drag to move, double-click to reset.
// Shaded with a fixed light (normals from the mesh, or computed when it has
// none); optional wireframe on top. Each submesh gets its own tint.

const VERTEX = `
attribute vec3 aPos;
attribute vec3 aNormal;
uniform mat4 uMvp;
uniform mat4 uModelView;
varying vec3 vNormal;
void main() {
  vNormal = mat3(uModelView) * aNormal;
  gl_Position = uMvp * vec4(aPos, 1.0);
}`;

const FRAGMENT = `
precision mediump float;
varying vec3 vNormal;
uniform vec3 uColor;
uniform float uLit;
void main() {
  vec3 n = normalize(vNormal);
  if (!gl_FrontFacing) n = -n;
  float light = 0.35 + 0.65 * max(dot(n, normalize(vec3(0.4, 0.7, 1.0))), 0.0);
  gl_FragColor = vec4(mix(uColor, uColor * light, uLit), 1.0);
}`;

const TINTS = [
  [0.72, 0.76, 0.82],
  [0.82, 0.72, 0.62],
  [0.62, 0.8, 0.68],
  [0.78, 0.66, 0.82],
  [0.62, 0.74, 0.86],
  [0.84, 0.8, 0.6],
];

/**
 * Parse a mesh preview blob: float32 positions (3 per vertex), float32
 * normals when `meta.has_normals`, then uint32 triangle indices.
 */
export function geometryFromPack(buffer, meta) {
  const n = meta.vertices * 3;
  const positions = new Float32Array(buffer, 0, n);
  const normals = meta.has_normals ? new Float32Array(buffer, n * 4, n) : null;
  const indices = new Uint32Array(buffer, n * 4 * (meta.has_normals ? 2 : 1), meta.triangles * 3);
  return { positions, normals, indices, submeshes: meta.submeshes };
}

/** Parse an OBJ (as the Mesh export writes it): v, vn and f lines; polygons become triangle fans. */
export function geometryFromObj(text) {
  const pos = [];
  const tris = [];
  const groups = [];
  let count = 0;
  for (const line of text.split("\n")) {
    if (line.startsWith("v ")) {
      const p = line.trim().split(/\s+/);
      pos.push(+p[1], +p[2], +p[3]);
    } else if (line.startsWith("f ")) {
      const verts = line.trim().split(/\s+/).slice(1).map((v) => {
        const i = parseInt(v, 10);
        return i < 0 ? pos.length / 3 + i : i - 1;
      });
      for (let k = 1; k + 1 < verts.length; k++) {
        tris.push(verts[0], verts[k], verts[k + 1]);
        count++;
      }
    } else if ((line.startsWith("g ") || line.startsWith("usemtl ")) && count) {
      groups.push(count);
      count = 0;
    }
  }
  if (count) groups.push(count);
  return { positions: new Float32Array(pos), normals: null, indices: new Uint32Array(tris), submeshes: groups.length ? groups : [tris.length / 3] };
}

/** Smooth normals: the sum of the faces around each vertex. */
function computeNormals(positions, indices) {
  const normals = new Float32Array(positions.length);
  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i] * 3, b = indices[i + 1] * 3, c = indices[i + 2] * 3;
    const ux = positions[b] - positions[a], uy = positions[b + 1] - positions[a + 1], uz = positions[b + 2] - positions[a + 2];
    const vx = positions[c] - positions[a], vy = positions[c + 1] - positions[a + 1], vz = positions[c + 2] - positions[a + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    for (const v of [a, b, c]) {
      normals[v] += nx;
      normals[v + 1] += ny;
      normals[v + 2] += nz;
    }
  }
  return normals;
}

/** Each edge once, for the wireframe. */
function edges(indices) {
  const seen = new Set();
  const out = [];
  const add = (a, b) => {
    const key = a < b ? a * 4294967296 + b : b * 4294967296 + a;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(a, b);
  };
  for (let i = 0; i < indices.length; i += 3) {
    add(indices[i], indices[i + 1]);
    add(indices[i + 1], indices[i + 2]);
    add(indices[i + 2], indices[i]);
  }
  return new Uint32Array(out);
}

// ----- 4x4 matrices (column-major, as WebGL takes them) -----

function perspective(fovy, aspect, near, far) {
  const f = 1 / Math.tan(fovy / 2);
  const nf = 1 / (near - far);
  return [f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0];
}

function multiply(a, b) {
  const out = new Array(16).fill(0);
  for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) for (let k = 0; k < 4; k++) out[c * 4 + r] += a[k * 4 + r] * b[c * 4 + k];
  return out;
}

/** The camera: orbit around `target` at `distance`, turned by yaw / pitch. */
function view(yaw, pitch, distance, target, pan) {
  const cy = Math.cos(yaw), sy = Math.sin(yaw), cp = Math.cos(pitch), sp = Math.sin(pitch);
  // rotation = Rx(pitch) * Ry(yaw), then move the target to the origin and back off by `distance`
  const rot = [cy, sy * sp, -sy * cp, 0, 0, cp, sp, 0, sy, -cy * sp, cy * cp, 0, 0, 0, 0, 1];
  const toOrigin = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, -target[0], -target[1], -target[2], 1];
  const back = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, pan[0], pan[1], -distance, 1];
  return multiply(back, multiply(rot, toOrigin));
}

export class MeshViewer {
  constructor(canvas) {
    this.canvas = canvas;
    this.wireframe = false;
    this.mesh = null;
    const gl = canvas.getContext("webgl2", { antialias: true }) || canvas.getContext("webgl", { antialias: true });
    this.gl = gl;
    if (!gl) return;
    // 32-bit indices: WebGL 2 has them, WebGL 1 needs the extension.
    this.uint32 = typeof WebGL2RenderingContext !== "undefined" && gl instanceof WebGL2RenderingContext ? true : Boolean(gl.getExtension("OES_element_index_uint"));
    this.program = this._program();
    this._bindInput();
    new ResizeObserver(() => this.draw()).observe(canvas);
  }

  get supported() {
    return Boolean(this.gl && this.uint32);
  }

  /** Show a geometry ({positions, normals, indices, submeshes}). */
  load(geometry) {
    this.clear();
    if (!this.supported) return;
    const gl = this.gl;
    const { positions, indices } = geometry;
    const normals = geometry.normals || computeNormals(positions, indices);
    const buffer = (target, data) => {
      const b = gl.createBuffer();
      gl.bindBuffer(target, b);
      gl.bufferData(target, data, gl.STATIC_DRAW);
      return b;
    };
    let min = [Infinity, Infinity, Infinity];
    let max = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < positions.length; i += 3) {
      for (let k = 0; k < 3; k++) {
        min[k] = Math.min(min[k], positions[i + k]);
        max[k] = Math.max(max[k], positions[i + k]);
      }
    }
    const center = min.map((v, k) => (v + max[k]) / 2);
    const radius = Math.max(1e-6, Math.hypot(max[0] - min[0], max[1] - min[1], max[2] - min[2]) / 2);
    const lines = edges(indices);
    this.mesh = {
      pos: buffer(gl.ARRAY_BUFFER, positions),
      nrm: buffer(gl.ARRAY_BUFFER, normals),
      idx: buffer(gl.ELEMENT_ARRAY_BUFFER, indices),
      lines: buffer(gl.ELEMENT_ARRAY_BUFFER, lines),
      lineCount: lines.length,
      submeshes: geometry.submeshes?.length ? geometry.submeshes : [indices.length / 3],
      center,
      radius,
      // Flat (2D) meshes are shown face on.
      flat: max[2] - min[2] < radius * 1e-3,
    };
    this.resetView();
  }

  clear() {
    if (!this.mesh || !this.gl) return;
    for (const b of [this.mesh.pos, this.mesh.nrm, this.mesh.idx, this.mesh.lines]) this.gl.deleteBuffer(b);
    this.mesh = null;
    this.draw();
  }

  resetView() {
    this.yaw = this.mesh?.flat ? 0 : -0.6;
    this.pitch = this.mesh?.flat ? 0 : 0.35;
    this.zoom = 1;
    this.pan = [0, 0];
    this.draw();
  }

  setWireframe(on) {
    this.wireframe = on;
    this.draw();
  }

  draw() {
    const gl = this.gl;
    if (!gl) return;
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round(this.canvas.clientWidth * dpr));
    const h = Math.max(1, Math.round(this.canvas.clientHeight * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    gl.viewport(0, 0, w, h);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    const m = this.mesh;
    if (!m) return;
    const fov = Math.PI / 4;
    // Fit the mesh in the narrower direction (a portrait phone is narrower than tall).
    const half = Math.atan(Math.tan(fov / 2) * Math.min(1, w / h));
    const distance = (m.radius / Math.sin(half)) * 1.1 * this.zoom;
    const modelView = view(this.yaw, this.pitch, distance, m.center, this.pan.map((p) => p * m.radius));
    const proj = perspective(fov, w / h, distance / 100, distance + m.radius * 4);
    const p = this.program;
    gl.useProgram(p.program);
    gl.uniformMatrix4fv(p.uMvp, false, multiply(proj, modelView));
    gl.uniformMatrix4fv(p.uModelView, false, modelView);
    gl.enable(gl.DEPTH_TEST);
    gl.bindBuffer(gl.ARRAY_BUFFER, m.pos);
    gl.enableVertexAttribArray(p.aPos);
    gl.vertexAttribPointer(p.aPos, 3, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, m.nrm);
    gl.enableVertexAttribArray(p.aNormal);
    gl.vertexAttribPointer(p.aNormal, 3, gl.FLOAT, false, 0, 0);
    // Faces, pushed back a little so the wireframe drawn after them stays visible.
    gl.enable(gl.POLYGON_OFFSET_FILL);
    gl.polygonOffset(1, 1);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.idx);
    gl.uniform1f(p.uLit, 1);
    let first = 0;
    m.submeshes.forEach((count, i) => {
      gl.uniform3fv(p.uColor, TINTS[i % TINTS.length]);
      gl.drawElements(gl.TRIANGLES, count * 3, gl.UNSIGNED_INT, first * 12);
      first += count;
    });
    gl.disable(gl.POLYGON_OFFSET_FILL);
    if (this.wireframe) {
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.lines);
      gl.uniform1f(p.uLit, 0);
      gl.uniform3fv(p.uColor, [0.12, 0.45, 0.85]);
      gl.drawElements(gl.LINES, m.lineCount, gl.UNSIGNED_INT, 0);
    }
  }

  _program() {
    const gl = this.gl;
    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || "shader");
      return s;
    };
    const program = gl.createProgram();
    gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT));
    gl.linkProgram(program);
    return {
      program,
      aPos: gl.getAttribLocation(program, "aPos"),
      aNormal: gl.getAttribLocation(program, "aNormal"),
      uMvp: gl.getUniformLocation(program, "uMvp"),
      uModelView: gl.getUniformLocation(program, "uModelView"),
      uColor: gl.getUniformLocation(program, "uColor"),
      uLit: gl.getUniformLocation(program, "uLit"),
    };
  }

  _bindInput() {
    const c = this.canvas;
    const pointers = new Map();
    let last = null;
    const panning = (e) => e.button === 2 || e.shiftKey || pointers.size === 2;
    c.addEventListener("contextmenu", (e) => e.preventDefault());
    c.addEventListener("pointerdown", (e) => {
      c.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      last = null;
    });
    const end = (e) => {
      pointers.delete(e.pointerId);
      last = null;
    };
    c.addEventListener("pointerup", end);
    c.addEventListener("pointercancel", end);
    c.addEventListener("pointermove", (e) => {
      if (!pointers.has(e.pointerId)) return;
      const prev = pointers.get(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      if (pointers.size === 2) {
        // Two fingers: pinch zooms, moving both pans.
        const [a, b] = [...pointers.values()];
        const spread = Math.hypot(a.x - b.x, a.y - b.y);
        if (last) this.zoom = Math.min(20, Math.max(0.05, this.zoom * (last / spread)));
        last = spread;
        this.pan[0] += dx / c.clientHeight;
        this.pan[1] -= dy / c.clientHeight;
      } else if (panning(e)) {
        this.pan[0] += (2 * dx) / c.clientHeight;
        this.pan[1] -= (2 * dy) / c.clientHeight;
      } else {
        this.yaw += dx * 0.01;
        this.pitch = Math.max(-1.55, Math.min(1.55, this.pitch + dy * 0.01));
      }
      this.draw();
    });
    c.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        this.zoom = Math.min(20, Math.max(0.05, this.zoom * Math.exp(e.deltaY * 0.001)));
        this.draw();
      },
      { passive: false },
    );
    c.addEventListener("dblclick", () => this.resetView());
  }
}
